import { useMemo } from 'react';
import type { ComponentType, ReactElement } from 'react';

/**
 * A hook that takes a component type and props and returns a memoized React element.
 * This is useful for creating reusable component instances with memoization to prevent
 * unnecessary re-renders when props haven't changed.
 * 
 * @template T - The props type for the component
 * @param Component - React component to render
 * @param props - Props to pass to the component
 * @returns Memoized React element
 * 
 * @example
 * ```tsx
 * const MyComponent = ({ title, count }) => (
 *   <div>
 *     <h1>{title}</h1>
 *     <p>Count: {count}</p>
 *   </div>
 * );
 * 
 * function ParentComponent() {
 *   const [count, setCount] = useState(0);
 *   
 *   // This view will only re-render when title or count changes
 *   const inflatedView = useInflateView(MyComponent, {
 *     title: 'My Title',
 *     count: count
 *   });
 *   
 *   return (
 *     <div>
 *       {inflatedView}
 *       <button onClick={() => setCount(c => c + 1)}>
 *         Increment
 *       </button>
 *     </div>
 *   );
 * }
 * ```
 */
export const useInflateView = <T extends ComponentType<any>>(
  Component: T,
  props: React.ComponentProps<T>
): ReactElement => {
  return useMemo(
    () => <Component {...props} />,
    [Component, props]
  );
};

export default useInflateView;